// deps
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

// internal-deps
import { B2C_BALANCE_ELIGIBILITY, BillToCompanyWidgetData } from "@swiggy-private/checkout-types";
import { isEmpty } from "@swiggy-private/js-utils";
import { useSessionStorage } from "@swiggy-private/checkout-core";
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";
import { DEFAULT_PAYMENTS_RESPONSE_KEY } from "@payment-client/constants/StorageKeys";
import { ShowPaymentCacheValue } from "@payment-client/interfaces/ShowPayments";
import { useBillToCompanyAnalyticsHandler } from "./useBillToCompanyAnalyticsHandler";
import { SCREEN_NAME } from "@payment-client/analytics/constants";

export interface UseBillToCompanyDetailsReturnType {
    onToggle: VoidFunction;
    onLoad: VoidFunction;
    billToCompanyData: BillToCompanyWidgetData | null;
    billToCompanyDetails: {
        selected: boolean;
        setBillToCompanyData: (data: BillToCompanyWidgetData | null) => void;
        isWidgetShown: boolean;
    };
}

export const useBillToCompanyDetails = (
    showExpenseCodeHalfCard?: boolean,
    onOpenHalfcard?: () => void,
    isSelected?: boolean,
    setIsSelected?: (isSelected: boolean) => void,
): UseBillToCompanyDetailsReturnType => {
    const [dataInCache] = useSessionStorage<ShowPaymentCacheValue | null>(DEFAULT_PAYMENTS_RESPONSE_KEY, null);
    const [billToCompanyData, setBillToCompanyData] = useState<BillToCompanyWidgetData | null>(null);

    const { loadingPayments, paymentResponse: contextPaymentResponse } = useContext(ShowPaymentContext);

    // Use cache data if context provides null or empty response
    const paymentResponse = contextPaymentResponse || dataInCache?.paymentResponse;

    const billToCompanyResponse: BillToCompanyWidgetData | null = useMemo(() => {
        if (loadingPayments || isEmpty(paymentResponse)) {
            return null;
        }
        return paymentResponse?.billToCompanyWidgetData || null;
    }, [loadingPayments, paymentResponse]);

    const { onImpressionBTCToggle } = useBillToCompanyAnalyticsHandler({ screenName: SCREEN_NAME.CHECKOUT });

    useEffect(() => {
        // Only update if the response actually changed to avoid unnecessary re-renders
        if (billToCompanyResponse !== billToCompanyData) {
            setBillToCompanyData(billToCompanyResponse);
        }
    }, [billToCompanyResponse, billToCompanyData]);

    const onToggle = useCallback(() => {
        if (showExpenseCodeHalfCard && !isSelected) {
            onOpenHalfcard && onOpenHalfcard();
            return;
        }
        setIsSelected && setIsSelected(!isSelected);
    }, [isSelected, showExpenseCodeHalfCard, onOpenHalfcard, setIsSelected]);

    const onLoad = useCallback(() => {
        const selected = isSelected || false;
        const context = JSON.stringify({
            balance: billToCompanyData?.balance,
            /* eslint-disable-next-line @typescript-eslint/naming-convention */
            balance_type: billToCompanyData?.balanceEligibility ?? B2C_BALANCE_ELIGIBILITY.UNLIMITED,
        });
        onImpressionBTCToggle(selected, context);
    }, [onImpressionBTCToggle, billToCompanyData, isSelected]);

    const billToCompanyDetails = useMemo(() => {
        return {
            selected: isSelected || false,
            setBillToCompanyData,
            isWidgetShown: !isEmpty(billToCompanyData),
        };
    }, [isSelected, setBillToCompanyData, billToCompanyData]);

    return { onToggle, onLoad, billToCompanyDetails, billToCompanyData };
};
